import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Dropdown, Menu } from 'antd';
import React, { FC, useState, useMemo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Timestamp } from 'api/models';
import { TaskStatusType } from 'api/status-updates';
import { TaskType } from 'api/tasks';
import { RangePicker } from 'components/molecules/range-picker';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { RootState } from 'data';
import { ResourceSectionType } from 'data/documents';
import {
  setActiveStatusUpdateHighlightsData,
  isTaskMatchesFilter,
} from 'data/status-updates';
import { FieldsStore, StatusField } from 'stores/fields';
import { INSERT_RESOURCE_LIST_SECTION_COMMAND } from 'vendor/ckeditor/plugins/resource-list-section';
import { Draggable } from '../../draggable';
import { Props, Count, getStatus, TaskListSkelton } from './highlight-helpers';
import styles from './highlight.module.css';

export const Highlight: FC<Props> = ({
  commandName = INSERT_RESOURCE_LIST_SECTION_COMMAND,
  commandOptions,
  tasks = [],
  getStructuredTasks,
  type,
}) => {
  const [date, setDate] = useState<[Timestamp | null, Timestamp | null]>([
    null,
    null,
  ]);

  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskStatusType>(
    TaskStatusType.Completed,
  );
  const dispatch = useDispatch();

  const handleMenuItemClick = (e) => {
    setTaskTypeFilter(e.key);
  };

  const menu = (
    <Menu onClick={handleMenuItemClick}>
      <Menu.Item key={TaskStatusType.Completed}>
        <FormattedMessage id={`tasks.${TaskStatusType.Completed}`} />
      </Menu.Item>
      {type !== ResourceSectionType.Approval ? (
        <Menu.Item key={TaskStatusType.Blocked}>
          <FormattedMessage id={`tasks.${TaskStatusType.Blocked}`} />
        </Menu.Item>
      ) : null}

      <Menu.Item key={TaskStatusType.Overdue}>
        <FormattedMessage id={`tasks.${TaskStatusType.Overdue}`} />
      </Menu.Item>
      <Menu.Item key={TaskStatusType.Upcoming}>
        <FormattedMessage id={`tasks.${TaskStatusType.Upcoming}`} />
      </Menu.Item>
      {type !== ResourceSectionType.Approval ? (
        <Menu.Item key={TaskStatusType.InProgress}>
          <FormattedMessage id={`tasks.${TaskStatusType.InProgress}`} />
        </Menu.Item>
      ) : null}
    </Menu>
  );

  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const status = FieldsStore.getTaskStatusField(fieldsStore)[1] as StatusField;

  const groupedTaskType = useMemo(() => {
    return {
      [TaskStatusType.Overdue]: tasks.filter((task) => {
        return isTaskMatchesFilter(TaskStatusType.Overdue, date, task);
      }),
      [TaskStatusType.Completed]: tasks.filter((task) => {
        return isTaskMatchesFilter(TaskStatusType.Completed, date, task);
      }),
      [TaskStatusType.Blocked]: tasks.filter((task) => {
        return isTaskMatchesFilter(TaskStatusType.Blocked, date, task, status);
      }),
      [TaskStatusType.Upcoming]: tasks.filter((task) => {
        return isTaskMatchesFilter(TaskStatusType.Upcoming, date, task);
      }),
      [TaskStatusType.InProgress]: tasks.filter((task) => {
        return isTaskMatchesFilter(
          TaskStatusType.InProgress,
          date,
          task,
          status,
        );
      }),
      resourceKey: 'taskId',
    };
  }, [tasks, date, status]);

  const resourceData = getStructuredTasks
    ? getStructuredTasks(date)
    : groupedTaskType;

  const resources = resourceData ? resourceData[taskTypeFilter] : [];
  const resourceIds = resources.map(
    (resource) => resource[resourceData.resourceKey],
  );

  useEffect(() => {
    dispatch(
      setActiveStatusUpdateHighlightsData({
        [type]: tasks,
      }),
    );
  }, [dispatch, type, tasks]);

  const renderConnectorTerm = () => {
    return (
      <>
        <FormattedMessage id="in" />
        {taskTypeFilter === TaskStatusType.Upcoming ? (
          <FormattedMessage id="next.connector" />
        ) : (
          <FormattedMessage id="past.connector" />
        )}
      </>
    );
  };

  return (
    <Draggable
      resourceIds={resourceIds}
      resourceObjects={resources}
      commandName={commandName}
      commandOptions={{
        ...commandOptions,
        resourceIds,
        resourceSectionFilterType: taskTypeFilter,
        resourceSectionFilterRange: date,
      }}
    >
      <div className={styles.card}>
        <div className={styles.header}>
          <Dropdown overlay={menu} trigger={['click']}>
            <span className={styles.title}>
              <FormattedMessage id={`tasks.${taskTypeFilter}`} />
              <ChevronDown16 />
            </span>
          </Dropdown>
          {![TaskStatusType.Blocked, TaskStatusType.InProgress].includes(
            taskTypeFilter,
          ) ? (
            <>
              <span className={styles.adder}>{renderConnectorTerm()}</span>

              <div className={styles.datepicker}>
                <RangePicker
                  onDateChange={(daterange) => setDate(daterange)}
                  isRangeForward={taskTypeFilter === TaskStatusType.Upcoming}
                />
              </div>
            </>
          ) : null}
        </div>
        <div className={styles.info}>
          <Count>
            <StatusIndicator
              status={getStatus(taskTypeFilter)}
              type={
                type === ResourceSectionType.Milestone
                  ? TaskType.Milestone
                  : TaskType.Task
              }
            />
            {resourceData[taskTypeFilter] &&
              resourceData[taskTypeFilter].length}
          </Count>
          <TaskListSkelton paragraph={{ rows: 3 }} title={false} />
        </div>
      </div>
    </Draggable>
  );
};
